// Settings > "Dictation microphone": the microphone every dictation mic
// button opens (AI Assistant, template assistant, note editor and the other
// free-text fields), separate from the recording microphone. For virtual
// visits where one input captures the call and a headset is for talking.
// Remembered per browser (device ids are per browser and origin).

const STORAGE_KEY = "rosc.dictationMicrophone";

export interface DictationMicrophone {
  deviceId: string;
  // Last known name, to show the choice while the device is unplugged.
  label: string;
}

const storage = (): Storage | null => {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
};

export const getDictationMicrophone = (): DictationMicrophone | null => {
  try {
    const raw = storage()?.getItem(STORAGE_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<DictationMicrophone> | null;
    return typeof value?.deviceId === "string" && value.deviceId
      ? { deviceId: value.deviceId, label: String(value.label ?? "") }
      : null;
  } catch {
    return null;
  }
};

// Null: "Same as recording".
export const setDictationMicrophone = (
  value: DictationMicrophone | null,
): void => {
  try {
    if (value) storage()?.setItem(STORAGE_KEY, JSON.stringify(value));
    else storage()?.removeItem(STORAGE_KEY);
  } catch {
    // Storage blocked (private mode): the choice lasts for this page only.
  }
};

// The device is gone or cannot be opened with that id (not a permission
// problem): dictation falls back to the default microphone.
const isMissingDevice = (error: unknown): boolean => {
  const name = (error as { name?: unknown } | null)?.name;
  return (
    name === "OverconstrainedError" ||
    name === "NotFoundError" ||
    name === "DevicesNotFoundError" ||
    name === "NotReadableError"
  );
};

export interface DictationStream {
  stream: MediaStream;
  // The chosen dictation microphone was missing; the default one is used.
  fellBack: boolean;
}

// Opens the microphone for one dictation: the dictation microphone when one
// is chosen, otherwise the recording microphone (`recordingDeviceId`, as a
// preference so a stale id never blocks dictation) or the default one.
export const openDictationStream = async ({
  recordingDeviceId,
  getUserMedia = (constraints) =>
    navigator.mediaDevices.getUserMedia(constraints),
  chosen = getDictationMicrophone(),
}: {
  recordingDeviceId?: string | null;
  getUserMedia?: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
  chosen?: DictationMicrophone | null;
} = {}): Promise<DictationStream> => {
  const fallback = (): Promise<MediaStream> =>
    getUserMedia({
      audio:
        recordingDeviceId && recordingDeviceId !== "default"
          ? { deviceId: recordingDeviceId }
          : true,
    });
  if (!chosen) return { stream: await fallback(), fellBack: false };
  try {
    const stream = await getUserMedia({
      audio: { deviceId: { exact: chosen.deviceId } },
    });
    return { stream, fellBack: false };
  } catch (error) {
    if (!isMissingDevice(error)) throw error;
    return { stream: await fallback(), fellBack: true };
  }
};

export interface MicrophoneInput {
  deviceId: string;
  label: string;
}

// Microphones the browser lists. Names stay empty until the site has had
// microphone access once; ids "default" and "communications" are aliases.
export const listMicrophones = async (): Promise<MicrophoneInput[]> => {
  if (!navigator.mediaDevices?.enumerateDevices) return [];
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices
    .filter(
      (device) =>
        device.kind === "audioinput" &&
        device.deviceId &&
        device.deviceId !== "default" &&
        device.deviceId !== "communications",
    )
    .map((device) => ({ deviceId: device.deviceId, label: device.label }));
};
